import React from 'react';
import Card from './Card.jsx';
import Input from './Input.jsx';

export const SA_PROVINCES = [
  'National',
  'Gauteng',
  'Western Cape',
  'KwaZulu-Natal',
  'Eastern Cape',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Free State',
];

export const INDUSTRIES = [
  'All Industries',
  'Mining & Quarrying',
  'Manufacturing',
  'Electricity, Gas & Water',
  'Construction',
  'Retail/Trade',
  'Transport, Storage & Communication',
  'Business Services & Real Estate',
  'Personal, Community & Social Services',
];

export const BEE_SECTOR = [
  'General Codes of Good Practice (incl May 2019 Amendments)',
  'ICT Sector',
  'MAC (Marketing, Advertising and Communication',
  'AgriBEE',
  'Finance Sector',
  'Construction',
  'Specialised General Codes of Good Practice',
  'Legal Sector Code',
  'Transport (Road Freight)',
  'Property',
  'Tourism',
];

export const ENTITY_SIZE = [
  'Generic',
  'QSE',
];

// Must match backend/apps/core/scorecard_totals.py's _LEVEL_ORDER exactly.
export const BEE_LEVELS = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6', 'Level 7', 'Level 8', 'Non-Compliant'];

export const EMPTY_COMPANY_INFO_FORM = {
  company_name: '',
  trading_name: '',
  registration_number: '',
  industry_norm: '',
  entity_size: '',
  bee_sector: '',
  bee_certificate_expiry: '',
  bee_level_achieved: '',
  province: '',
  financial_year_start: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  designation: '',
};

export function validateCompanyInfo(form) {
  const errs = {};
  if (!form.company_name.trim()) errs.company_name = 'Company name is required';
  if (!form.registration_number.trim()) errs.registration_number = 'Registration number is required';
  if (!form.province) errs.province = 'Please select a province';
  if (!form.industry_norm) errs.industry_norm = 'Select Industry/Sector';
  if (!form.entity_size) errs.entity_size = 'Select size of Entity';
  if (!form.bee_sector) errs.bee_sector = 'Select BEE Sector Code';
  if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
    errs.contact_email = 'Please enter a valid email address';
  }
  if (!form.designation) errs.designation = 'Designation at Company is required';
  return errs;
}

export const calculateEndDate = (startDate) => {
    const start = new Date(`${startDate}T00:00:00`);
    if (Number.isNaN(start.getTime())) return '';
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);
    const yyyy = end.getFullYear();
    const mm = String(end.getMonth() + 1).padStart(2, '0');
    const dd = String(end.getDate()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd}`;
  };

const CompanyInfoFields = ({ form, errors, onChange, canEdit = true, label, onLabelChange }) => {
  return (
    <>
      {/* Section: Registration */}
      <Card title="Registration Details" className="ci-card">
        <div className="ci-grid">
          <Input
            label="Registered Company Name"
            name="company_name"
            disabled={!canEdit}
            placeholder="As per CIPC registration"
            value={form.company_name}
            onChange={onChange}
            error={errors.company_name}
            required
          />
          <Input
            label="Trading Name"
            name="trading_name"
            disabled={!canEdit}
            placeholder="If different from registered name"
            value={form.trading_name}
            onChange={onChange}
          />
          <Input
            label="Registration Number"
            name="registration_number"
            disabled={!canEdit}
            placeholder="e.g. 2020/123456/07"
            value={form.registration_number}
            onChange={onChange}
            error={errors.registration_number}
            required
          />
          <div className="ci-field">
            <label className="input-label">Industry / Sector</label>
            <select
              disabled={!canEdit}
              name="industry_norm"
              value={form.industry_norm}
              onChange={onChange}
              className="ci-select"
            >
              <option value="">— Select industry —</option>
              {INDUSTRIES.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Section: BEE Classification */}
      <Card title="BEE Classification" className="ci-card">
        <div className="ci-grid">
          <div className="ci-field">
            <label className="input-label">BEE Sector Code <span className="ci-required">*</span></label>
            <select
              disabled={!canEdit}
              name="bee_sector"
              value={form.bee_sector}
              onChange={onChange}
              className={`ci-select${errors.bee_sector ? ' ci-select--error' : ''}`}
            >
              <option value="">— Select BEE Sector —</option>
              {BEE_SECTOR.map(sector => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
            {errors.bee_sector && <span className="error-message">{errors.bee_sector}</span>}
          </div>
          <div className="ci-field">
            <label className="input-label">Entity Size <span className="ci-required">*</span></label>
            <select
              disabled={!canEdit}
              name="entity_size"
              value={form.entity_size}
              onChange={onChange}
              className={`ci-select${errors.entity_size ? ' ci-select--error' : ''}`}
            >
              <option value="">— Select Entity Size —</option>
              {ENTITY_SIZE.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            {errors.entity_size && <span className="error-message">{errors.entity_size}</span>}
          </div>
        </div>
      </Card>

      {/* Section: Province — drives EAP targets */}
      <Card title="Province" className="ci-card">
        <p className="ci-province-note">
          Select the province where the majority of your workforce is based, or <strong>National</strong> if your operations span multiple provinces. This drives the EAP demographic targets used in your Management Control and Skills Development scorecards.
        </p>
        <div className="ci-province-grid">
          {SA_PROVINCES.map(prov => (
            <label
              key={prov}
              className={`ci-province-tile ${form.province === prov ? 'ci-province-tile--selected' : ''}`}
            >
              <input
                type="radio"
                name="province"
                value={prov}
                checked={form.province === prov}
                disabled={!canEdit}
                onChange={onChange}
              />
              <span>{prov}</span>
            </label>
          ))}
        </div>
        {errors.province && <span className="error-message">{errors.province}</span>}
      </Card>

      {/* Section: Financial */}
      <Card title="Certificate Expiry" className="ci-card">
        <div className='ci-grid'>
          <div className='ci-field'>
            <label className="input-label"> BEE Certificate Expiry</label>
            <Input
              name="bee_certificate_expiry"
              type="date"
              value={form.bee_certificate_expiry}
              onChange={onChange}
            />
          </div>
          <div className="ci-field">
            <label className="input-label">BEE Level Achieved</label>
            <select
              disabled={!canEdit}
              name="bee_level_achieved"
              value={form.bee_level_achieved}
              onChange={onChange}
              className="ci-select"
            >
              <option value="">— Not recorded —</option>
              {BEE_LEVELS.map(lvl => (
                <option key={lvl} value={lvl}>{lvl}</option>
              ))}
            </select>
            <span className="ci-field-hint">
              The level on the certificate currently on file (same certificate the expiry date
              above refers to). Used as this financial year's Y.E.S "prior level maintained" baseline.
            </span>
          </div>
        </div>
      </Card>

      <Card title="Financial Details" className="ci-card">
        <div className="ci-grid">
          <div className="ci-field">
            <label className="input-label">Financial Year Start</label>
            <Input
              name="financial_year_start"
              type="date"
              value={form.financial_year_start}
              onChange={onChange}
              error={errors.financial_year_start}
            />
          </div>
          <div className="ci-field">
            <label className="input-label">Financial Year End</label>
            <Input
              name="financial_year_end"
              type="text"
              value={calculateEndDate(form.financial_year_start)}
              disabled
            />
          </div>
          {onLabelChange && (
            <div className="ci-field">
              <label className="input-label">
                Label <span className="form-hint">(optional — defaults to FY year)</span>
              </label>
              <Input
                name="label"
                type="text"
                placeholder="e.g. FY2025"
                value={label}
                onChange={onLabelChange}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Section: Contact */}
      <Card title="Contact Person" className="ci-card">
        <div className="ci-grid">
          <Input
            label="Contact Person"
            name="contact_person"
            placeholder="Full name"
            value={form.contact_person}
            onChange={onChange}
          />
          <Input
            label="Contact Email"
            name="contact_email"
            type="email"
            placeholder="email@company.co.za"
            value={form.contact_email}
            onChange={onChange}
            error={errors.contact_email}
          />
          <Input
            label="Contact Phone"
            name="contact_phone"
            placeholder="+27 xx xxx xxxx"
            value={form.contact_phone}
            onChange={onChange}
          />
          <Input
            label="Designation"
            name="designation"
            placeholder="HR Manager"
            value={form.designation}
            onChange={onChange}
            error={errors.designation}
          />
        </div>
      </Card>
    </>
  );
};

export default CompanyInfoFields;
